"use client";

import { Check, Loader2, ScrollText } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";
import { useSearchVisaTypeQuery } from "../../../search/api/visa-search-api";
import { IVisaTypeOption } from "../../../search/types/visa.types";

interface VisaTypeSelectProps {
  value?: IVisaTypeOption | null;
  onChange: (value: IVisaTypeOption | null) => void;
  label: string;
  placeholder: string;
  invalid?: boolean;
  disabled?: boolean;
  popoverModal?: boolean;
}

export function VisaTypeSelect({
  value,
  onChange,
  label,
  placeholder,
  invalid = false,
  popoverModal = false,
  disabled = false,
}: VisaTypeSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useSearchVisaTypeQuery(
    {
      name: debouncedSearchTerm || undefined,
      limit: 20,
      skip: 0,
    },
    {
      refetchOnReconnect: false,
      refetchOnMountOrArgChange: true,
    },
  );

  const visaTypes = data?.data ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen} modal={popoverModal}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-invalid={invalid}
          disabled={disabled}
          className={cn(
            "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
            !value?.name && "text-muted-foreground",
            invalid && "border-destructive",
          )}
        >
          {value?.name ? (
            <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
              <span className="text-base font-bold tracking-tight text-foreground sm:text-[1.05rem]">
                {value.name.slice(0, 3).toUpperCase()}
              </span>
              <span className="h-9 w-px bg-border" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground/85">
                  {value.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  Visa type
                </span>
              </span>
            </span>
          ) : (
            <span className="min-w-0 text-left">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                {label}
              </span>
              <span className="block truncate text-sm text-muted-foreground">
                {placeholder}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className="p-0 h-52 md:h-full" align="start">
        <Command shouldFilter={false}>
          <div className="relative">
            <CommandInput
              value={searchTerm}
              onValueChange={setSearchTerm}
              placeholder="Search visa types..."
            />
            {(isLoading || isFetching) && (
              <Loader2 className="text-muted-foreground absolute right-2 top-2 h-4 w-4 animate-spin" />
            )}
          </div>

          <CommandList className="no-scrollbar overscroll-contain touch-pan-y">
            <CommandEmpty>
              {isLoading || isFetching ? "Loading..." : "No results found"}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="all-visa-types"
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className={cn(
                  "w-full cursor-pointer truncate border-b px-2 py-2",
                  !value?.name && "bg-primary text-primary-foreground",
                )}
              >
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium">All visa types</span>
                  <Check
                    className={cn(
                      "h-4 w-4",
                      !value?.name ? "opacity-100" : "opacity-0",
                    )}
                  />
                </div>
              </CommandItem>

              {visaTypes.map((visaType, index) => {
                const isSelected =
                  visaType.id !== undefined && value?.id !== undefined
                    ? String(visaType.id) === String(value.id)
                    : visaType.name === value?.name;

                return (
                  <CommandItem
                    key={String(visaType.id ?? index)}
                    value={String(visaType.id ?? visaType.name)}
                    onSelect={() => {
                      onChange({
                        id: visaType.id,
                        name: visaType.name,
                      });
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full cursor-pointer truncate border-b px-2 py-2",
                      isSelected && "bg-primary text-primary-foreground",
                    )}
                  >
                    <div className="flex w-full items-center gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2 tracking-tight">
                        <ScrollText
                          className={cn(
                            "size-3.5 text-primary",
                            isSelected && "text-primary-foreground",
                          )}
                        />
                        <h3
                          className={cn(
                            "truncate font-medium",
                            isSelected
                              ? "text-primary-foreground"
                              : "text-primary",
                          )}
                        >
                          {visaType.name}
                        </h3>
                      </div>

                      <Check
                        className={cn(
                          "h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
